import React from 'react';
import PropTypes from 'prop-types';

/**
 * Avatar component for displaying user profile pictures
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Alternative text for the image
 * @param {string} props.size - Size of the avatar (xs, sm, md, lg, xl)
 * @param {boolean} props.isOnline - Whether the user is online
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} Avatar component
 */
const Avatar = ({ src, alt, size = 'md', isOnline = false, className = '' }) => {
  // Define size classes
  const sizeClasses = {
    xs: 'w-6 h-6',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24',
    '2xl': 'w-32 h-32',
  };
  
  // Get initials from alt text for fallback
  const getInitials = () => {
    if (!alt) return '';
    
    const words = alt.split(' ');
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    
    return (
      words[0].charAt(0).toUpperCase() + 
      words[words.length - 1].charAt(0).toUpperCase()
    );
  };
  
  // Determine background color based on alt text for consistent colors
  const getBackgroundColor = () => {
    if (!alt) return 'bg-gray-400';
    
    const colors = [
      'bg-red-500',
      'bg-green-500',
      'bg-blue-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    
    // Hash the name to get a consistent color
    const charCodeSum = [...alt].reduce(
      (sum, char) => sum + char.charCodeAt(0),
      0
    );
    
    return colors[charCodeSum % colors.length];
  };
  
  return (
    <div className={`relative inline-block ${className}`}>
      {src ? (
        <img
          src={src}
          alt={alt}
          className={`${sizeClasses[size]} rounded-full object-cover border border-gray-200`}
        />
      ) : (
        <div
          className={`${sizeClasses[size]} ${getBackgroundColor()} rounded-full flex items-center justify-center text-white font-medium`}
          style={{ fontSize: size === 'xs' ? '0.5rem' : size === 'sm' ? '0.75rem' : undefined }}
        >
          {getInitials()}
        </div>
      )}
      
      {isOnline && (
        <span
          className={`absolute bottom-0 right-0 rounded-full bg-green-500 border-2 border-white ${
            size === 'xs' || size === 'sm' ? 'w-2 h-2' : 'w-3 h-3'
          }`}
        ></span>
      )}
    </div>
  );
};

Avatar.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl']),
  isOnline: PropTypes.bool,
  className: PropTypes.string,
};

export default Avatar;